/**
 * The notification bell.
 *
 * Scope keeps its own list rather than pushing onto Cloud's — see
 * worker/lib/notify.ts for why that direction is the right way round.
 *
 * Dismissal follows the same contract the sibling apps use for every popover:
 * outside mousedown, and Escape.
 */

import { useEffect, useRef, useState } from 'react';
import { ds } from '../ds';
import { useRouter } from '../router';
import { api } from '../api';
import { RADIUS, formatWhen, mono, monoSmall } from '../theme';

interface Item {
  id: string;
  kind: string;
  projectId: string;
  subject: string;
  actorName: string | null;
  readAt: number | null;
  createdAt: number;
}

const SENTENCE: Record<string, string> = {
  scope_submitted: 'finished the intake for',
  scope_followup_answered: 'answered your follow-ups on'
};

export function Bell() {
  const { Icon } = ds();
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [unread, setUnread] = useState(0);
  const anchor = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const res = await api.notifications.list();
      setItems(res.notifications);
      setUnread(res.unread);
    } catch {
      // A bell that cannot load is not worth an error screen.
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (anchor.current && !anchor.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const openItem = async (item: Item) => {
    setOpen(false);
    await api.notifications.markRead(item.id).catch(() => {});
    setUnread((n) => Math.max(0, n - (item.readAt ? 0 : 1)));
    navigate(`/p/${item.projectId}`);
  };

  return (
    <div ref={anchor} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={unread ? `${unread} unread` : 'Notifications'}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) void load();
        }}
        style={{
          border: 0,
          background: 'transparent',
          color: 'var(--text-muted)',
          padding: 6,
          borderRadius: RADIUS.chip,
          display: 'flex',
          position: 'relative'
        }}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--signal)'
            }}
          />
        )}
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 40,
            right: 0,
            width: 'min(340px, calc(100vw - 32px))',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: RADIUS.card,
            boxShadow: '0 18px 44px rgba(8,6,16,.4)',
            overflow: 'hidden',
            zIndex: 6
          }}
        >
          <div
            style={{
              ...monoSmall,
              padding: '14px 16px 10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => {
                  void api.notifications.markRead().then(load);
                }}
                style={{ ...mono, fontSize: 9, border: 0, background: 'transparent', color: 'var(--primary-text)' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p style={{ margin: 0, padding: '4px 16px 18px', fontSize: 13.5, color: 'var(--text-faint)' }}>
              Nothing yet.
            </p>
          ) : (
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="mv-row"
                  onClick={() => void openItem(item)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 0,
                    background: item.readAt ? 'transparent' : 'var(--surface-inset)',
                    padding: '12px 16px',
                    borderTop: '1px solid var(--border-subtle)'
                  }}
                >
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-body)' }}>
                    <strong style={{ fontWeight: 600 }}>{item.actorName ?? 'Someone'}</strong>{' '}
                    {SENTENCE[item.kind] ?? 'updated'} {item.subject}
                  </div>
                  <div style={{ ...mono, fontSize: 9, marginTop: 6 }}>{formatWhen(item.createdAt)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
